/**
 * Question editor — own tab (big form: text, type, options editor, scoring, explanation…).
 *   /instructor/questions/new              → tab 'question:new'
 *   /instructor/questions/:questionId/edit → tab 'question:edit:<id>'
 * Categories and courses are loaded by the form itself. A `?course_id=` query on the "new"
 * route pre-selects the course (the list passes its current course filter).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabs, useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichTextEditor from '../../components/richtext/RichTextEditor';
import FigureField from '../../components/questions/FigureField';
import { stripHtml } from '../../components/richtext/RichText';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Trash,
  Upload,
  X,
  CheckCircle,
  FileText,
  Question,
  ListNumbers,
  TextT,
  Hash,
  Equals,
  SortAscending,
  Star,
  ChatText,
  FloppyDisk,
  ListChecks,
  Gauge,
} from '@phosphor-icons/react';

export const QUESTION_TYPES = [
  { value: 'MCQ_SINGLE', label: 'Multiple Choice (Single)', icon: CheckCircle },
  { value: 'MCQ_MULTI', label: 'Multiple Choice (Multi)', icon: ListNumbers },
  { value: 'TRUE_FALSE', label: 'True/False', icon: CheckCircle },
  { value: 'FILL_BLANK', label: 'Fill in the Blank', icon: TextT },
  { value: 'SHORT_ANSWER', label: 'Short Answer', icon: TextT },
  { value: 'ESSAY', label: 'Essay', icon: FileText },
  { value: 'NUMERIC', label: 'Numeric', icon: Hash },
  { value: 'MATCH', label: 'Matching', icon: Equals },
  { value: 'ORDER', label: 'Ordering', icon: SortAscending },
  { value: 'FILE_UPLOAD', label: 'File Upload', icon: Upload },
  { value: 'RATING', label: 'Rating (Survey)', icon: Star },
  { value: 'TEXT_FEEDBACK', label: 'Text Feedback (Survey)', icon: ChatText },
];

export const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', color: 'bg-green-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'hard', label: 'Hard', color: 'bg-red-500' },
];

export const initialQuestionForm = {
  question_text: '',
  question_type: 'MCQ_SINGLE',
  course_id: '',
  node_id: '',
  category_id: '',
  topic: '',
  difficulty: 'medium',
  tags: '',
  points: 1,
  negative_marks: 0,
  time_limit_seconds: null,
  options: [
    { id: 'opt1', text: '', is_correct: false },
    { id: 'opt2', text: '', is_correct: false },
    { id: 'opt3', text: '', is_correct: false },
    { id: 'opt4', text: '', is_correct: false },
  ],
  correct_answer: '',
  correct_answers: '',
  numeric_answer: null,
  numeric_tolerance: 0,
  explanation: '',
  hint: '',
};

const LIST_ROUTE = '/instructor/questions';

/** API record → form state (same mapping the list's edit handler used). */
const questionToForm = (question) => ({
  ...initialQuestionForm,
  ...question,
  course_id: question.course_id || '',
  node_id: question.node_id || '',
  category_id: question.category_id || '',
  tags: question.tags?.join(', ') || '',
  correct_answers: question.correct_answers?.join(', ') || '',
  options: question.options?.length > 0 ? question.options : initialQuestionForm.options,
});

export default function QuestionForm() {
  const { questionId } = useParams();
  const isNew = !questionId;
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const { closeAndNavigate } = useTabs();
  useTabLabel(isNew ? 'New question' : 'Edit question');

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  // New questions default to the course the list was filtered on (if any); editable in the form.
  const [questionForm, setQuestionForm] = useState({ ...initialQuestionForm, course_id: searchParams.get('course_id') || '', node_id: searchParams.get('node_id') || '', category_id: searchParams.get('category_id') || '' });
  const [courseNodes, setCourseNodes] = useState([]); // chapters / topics / subtopics of the chosen course
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/courses');
      setCourses(res.data);
    } catch {
      console.error('Failed to load courses');
    }
  }, [api]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch {
      console.error('Failed to load categories');
    }
  }, [api]);

  const fetchQuestion = useCallback(async () => {
    if (!questionId) return;
    setLoading(true);
    try {
      const res = await api.get(`/question-bank/${questionId}`);
      setEditingQuestion(res.data);
      setQuestionForm(questionToForm(res.data));
    } catch {
      toast.error('Failed to load question');
      navigate(LIST_ROUTE);
    } finally {
      setLoading(false);
    }
  }, [api, questionId, navigate]);

  useEffect(() => {
    fetchCourses();
    fetchCategories();
    fetchQuestion();
  }, [fetchCourses, fetchCategories, fetchQuestion]);

  useEffect(() => {
    let cancelled = false;
    if (!questionForm.course_id) { setCourseNodes([]); return undefined; }
    api.get(`/courses/${questionForm.course_id}/nodes?flat=true`)
      .then((r) => { if (!cancelled) setCourseNodes(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setCourseNodes([]); });
    return () => { cancelled = true; };
  }, [api, questionForm.course_id]);

  // flatten nodes (parent before children) with depth for the indented picker
  const nodeOptions = React.useMemo(() => {
    const byParent = {};
    courseNodes.forEach((n) => { (byParent[n.parent_id || '__root'] = byParent[n.parent_id || '__root'] || []).push(n); });
    const out = [];
    const walk = (pid, depth) => (byParent[pid || '__root'] || []).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).forEach((n) => { out.push({ ...n, depth }); walk(n.id, depth + 1); });
    walk(null, 0);
    return out;
  }, [courseNodes]);

  const handleSaveQuestion = async () => {
    if (!stripHtml(questionForm.question_text).trim()) {
      toast.error('Question text is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...questionForm,
        tags: questionForm.tags ? questionForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        correct_answers: questionForm.correct_answers ? questionForm.correct_answers.split(',').map(t => t.trim()).filter(Boolean) : [],
        options: questionForm.options.filter(opt => opt.text.trim()),
        // Use the form's own course (not the list filter) so editing under "All courses" never wipes it.
        course_id: questionForm.course_id || null,
        node_id: questionForm.course_id ? (questionForm.node_id || null) : null,
        category_id: questionForm.category_id || null,
      };

      if (editingQuestion) {
        await api.put(`/question-bank/${editingQuestion.id}`, payload);
        toast.success('Question updated');
      } else {
        await api.post('/question-bank', payload);
        toast.success('Question created');
        // close the "New question" tab and return to the list
        closeAndNavigate('question:new', LIST_ROUTE);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (isNew) return;
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await api.delete(`/question-bank/${questionId}`);
      toast.success('Question deleted');
      closeAndNavigate(`question:edit:${questionId}`, LIST_ROUTE);
    } catch {
      toast.error('Failed to delete question');
    }
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...questionForm.options];
    if (field === 'is_correct' && questionForm.question_type === 'MCQ_SINGLE') {
      newOptions.forEach((opt, i) => {
        opt.is_correct = i === index ? value : false;
      });
    } else {
      newOptions[index] = { ...newOptions[index], [field]: value };
    }
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const addOption = () => {
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, { id: `opt${Date.now()}`, text: '', is_correct: false }]
    });
  };

  const removeOption = (index) => {
    setQuestionForm({
      ...questionForm,
      options: questionForm.options.filter((_, i) => i !== index)
    });
  };

  const needsOptions = ['MCQ_SINGLE', 'MCQ_MULTI', 'TRUE_FALSE', 'MATCH', 'ORDER'].includes(questionForm.question_type);
  const needsCorrectAnswer = ['FILL_BLANK', 'SHORT_ANSWER'].includes(questionForm.question_type);
  const needsNumericAnswer = questionForm.question_type === 'NUMERIC';
  const typeLabel = QUESTION_TYPES.find(t => t.value === questionForm.question_type)?.label || questionForm.question_type;

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="question-form"
        title={isNew ? 'New question' : 'Edit question'}
        subtitle={isNew ? 'Fill in the details and save to add it to the question bank' : `${typeLabel} · ${editingQuestion?.topic || 'no topic'}`}
        onBack={() => navigate(LIST_ROUTE)}
        onRefresh={isNew ? undefined : fetchQuestion}
        refreshing={loading}
        loading={loading}
       
        primary={[
          { key: 'save', label: saving ? 'Saving…' : isNew ? 'Create question' : 'Save', icon: FloppyDisk, onClick: handleSaveQuestion, disabled: saving, testid: 'save-question-btn' },
          { key: 'cancel', label: 'Cancel', variant: 'outline', onClick: () => navigate(LIST_ROUTE), testid: 'cancel-question-btn' },
        ]}
        secondary={isNew ? [] : [
          { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: handleDeleteQuestion, testid: 'delete-question-btn' },
        ]}
      >
        <FormSection title="Question" icon={Question}>
          <div className="space-y-4">
            <div className="field">
              <Label>Question Text *</Label>
              <RichTextEditor
                value={questionForm.question_text}
                onChange={(html) => setQuestionForm({ ...questionForm, question_text: html })}
                placeholder="Type the question. Use the toolbar for formatting and sub/superscript (x², H₂O)."
                minHeight={110}
                testid="question-text-editor"
              />
            </div>
            <FigureField
              value={questionForm.media_url}
              onChange={(v) => setQuestionForm({ ...questionForm, media_url: v })}
              testid="question-figure"
            />

            <div className="form-grid">
              <div>
                <Label>Question Type</Label>
                <Select value={questionForm.question_type} onValueChange={(v) => setQuestionForm({ ...questionForm, question_type: v })}>
                  <SelectTrigger data-testid="question-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={questionForm.category_id || "none"} onValueChange={(v) => setQuestionForm({ ...questionForm, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="question-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                          {'\u00a0\u00a0\u00a0'.repeat(c.depth || 0)}{c.depth ? '└ ' : ''}{c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            <div className="field">
              <Label>Course</Label>
              <Select value={questionForm.course_id || "none"} onValueChange={(v) => setQuestionForm({ ...questionForm, course_id: v === "none" ? "" : v, node_id: '' })}>
                <SelectTrigger data-testid="question-course-select">
                  <SelectValue placeholder="Select course" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Course</SelectItem>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {questionForm.course_id && (
              <div className="field">
                <Label>Chapter / Topic / Subtopic</Label>
                <select className="form-select" value={questionForm.node_id || ''} onChange={(e) => setQuestionForm({ ...questionForm, node_id: e.target.value })} data-testid="question-node-select">
                  <option value="">— Whole course —</option>
                  {nodeOptions.map((n) => (
                    <option key={n.id} value={n.id}>{'\u00A0\u00A0'.repeat(n.depth)}{n.depth > 0 ? '└ ' : ''}{n.title}{n.node_type === 'section' ? ' (chapter)' : n.node_type === 'subsection' ? ' (topic)' : ''}</option>
                  ))}
                </select>
                {nodeOptions.length === 0 && <p className="field-hint">This course has no chapters yet — build its structure to file questions by chapter / topic.</p>}
              </div>
            )}
            </div>
          </div>
        </FormSection>

        <FormSection title="Answer" icon={ListChecks}>
          <div className="space-y-4">
            {/* Options for MCQ/True-False/Match/Order */}
            {needsOptions && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Options</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addOption}>
                    <Plus className="w-3 h-3 mr-1" /> Add Option
                  </Button>
                </div>
                <div className="space-y-2">
                  {questionForm.options.map((opt, idx) => (
                    <div key={opt.id} className="flex items-center gap-2">
                      <input
                        type={questionForm.question_type === 'MCQ_MULTI' ? 'checkbox' : 'radio'}
                        name="correct_option"
                        checked={opt.is_correct}
                        onChange={(e) => updateOption(idx, 'is_correct', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Input
                        value={opt.text}
                        onChange={(e) => updateOption(idx, 'text', e.target.value)}
                        placeholder={`Option ${idx + 1}`}
                        className="flex-1"
                      />
                      {questionForm.question_type === 'MATCH' && (
                        <Input
                          value={opt.match_target || ''}
                          onChange={(e) => updateOption(idx, 'match_target', e.target.value)}
                          placeholder="Matches to..."
                          className="w-32"
                        />
                      )}
                      {questionForm.question_type === 'ORDER' && (
                        <Input
                          type="number"
                          value={opt.order_position || idx + 1}
                          onChange={(e) => updateOption(idx, 'order_position', parseInt(e.target.value))}
                          placeholder="Position"
                          className="w-20"
                        />
                      )}
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeOption(idx)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Text-based correct answer */}
            {needsCorrectAnswer && (
              <div>
                <Label>Correct Answer(s)</Label>
                <Input
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                  placeholder="Enter the correct answer"
                />
                <Input
                  value={questionForm.correct_answers}
                  onChange={(e) => setQuestionForm({ ...questionForm, correct_answers: e.target.value })}
                  placeholder="Alternative answers (comma-separated)"
                  className="mt-2"
                />
              </div>
            )}

            {/* Numeric answer */}
            {needsNumericAnswer && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Correct Numeric Value</Label>
                  <Input
                    type="number"
                    step="any"
                    value={questionForm.numeric_answer || ''}
                    onChange={(e) => setQuestionForm({ ...questionForm, numeric_answer: parseFloat(e.target.value) })}
                    placeholder="e.g., 42"
                  />
                </div>
                <div>
                  <Label>Tolerance (+/-)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={questionForm.numeric_tolerance}
                    onChange={(e) => setQuestionForm({ ...questionForm, numeric_tolerance: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            )}

            {!needsOptions && !needsCorrectAnswer && !needsNumericAnswer && (
              <p className="field-hint">This question type has no predefined answer key — responses are reviewed manually.</p>
            )}

            <div>
              <Label>Explanation (shown after answer)</Label>
              <RichTextEditor
                compact
                value={questionForm.explanation}
                onChange={(html) => setQuestionForm({ ...questionForm, explanation: html })}
                placeholder="Explain why this is the correct answer..."
                minHeight={72}
                testid="question-explanation-editor"
              />
            </div>

            <div>
              <Label>Hint (optional)</Label>
              <Input
                value={questionForm.hint}
                onChange={(e) => setQuestionForm({ ...questionForm, hint: e.target.value })}
                placeholder="Give students a hint..."
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Scoring & metadata" icon={Gauge}>
          <div className="space-y-4">
            <div className="form-grid">
              <div>
                <Label>Difficulty</Label>
                <Select value={questionForm.difficulty} onValueChange={(v) => setQuestionForm({ ...questionForm, difficulty: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Points</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={questionForm.points}
                  onChange={(e) => setQuestionForm({ ...questionForm, points: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Negative Marks</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={questionForm.negative_marks}
                  onChange={(e) => setQuestionForm({ ...questionForm, negative_marks: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="form-grid">
              <div>
                <Label>Topic</Label>
                <Input
                  value={questionForm.topic}
                  onChange={(e) => setQuestionForm({ ...questionForm, topic: e.target.value })}
                  placeholder="e.g., Mathematics"
                />
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={questionForm.tags}
                  onChange={(e) => setQuestionForm({ ...questionForm, tags: e.target.value })}
                  placeholder="e.g., algebra, basics"
                />
              </div>
            </div>
          </div>
        </FormSection>
      </RecordPage>
    </DashboardLayout>
  );
}
